/**
 * ExploreScreen.jsx — Phase 4 (Upgraded to MobileJournal)
 *
 * 6-chip filter strip with one section visible at a time.
 * Imports existing components DIRECTLY — no logic modification.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import {
  collection, addDoc, onSnapshot, deleteDoc, doc,
  query, where, Timestamp
} from "firebase/firestore";

// ── NEW: Import Context ──
import { useAppShell } from "../AppShellContext";

// ── Import existing components ──
import Exercises from "../../components/Exercises";
import JokesSection from "../../components/Jokessection";
import WellnessTests from "../../components/Wellnesstests";

// ── NEW: Import MobileJournal instead of StoryWriter ──
import MobileJournal from "./MobileJournal";

// ── Chip definitions ──
const CHIPS = [
  { id: "stories",   emoji: "📚", label: "Stories" },
  { id: "games",     emoji: "🎮", label: "Games" },
  { id: "gratitude", emoji: "🙏", label: "Gratitude" },
  { id: "exercises", emoji: "💪", label: "Exercises" },
  { id: "jokes",     emoji: "😂", label: "Jokes" },
  { id: "assess",    emoji: "🧪", label: "Assessments" },
];

const MINI_GAMES = [
  { id: "ttt",      emoji: "❌⭕", name: "Tic Tac Toe",  desc: "vs AI" },
  { id: "memory",   emoji: "🃏",   name: "Memory Match",  desc: "Find pairs" },
  { id: "scramble", emoji: "🔤",   name: "Word Scramble", desc: "20 levels" },
  { id: "snake",    emoji: "🐍",   name: "Snake",         desc: "Classic" },
  { id: "reaction", emoji: "⚡",   name: "Reaction Test", desc: "Click fast" },
  { id: "guess",    emoji: "🔢",   name: "Number Guess",  desc: "1-100" },
  { id: "math",     emoji: "🧮",   name: "Math Sprint",   desc: "40 levels" },
  { id: "duel",     emoji: "⚔️",   name: "2P Duel",       desc: "Challenge" },
];

const NOTE_COLORS = [
  { bg: "#fef9c3", color: "#713f12", border: "rgba(234,179,8,.25)" },
  { bg: "#fce7f3", color: "#9d174d", border: "rgba(236,72,153,.2)" },
  { bg: "#dcfce7", color: "#14532d", border: "rgba(34,197,94,.2)" },
  { bg: "#dbeafe", color: "#1e3a8a", border: "rgba(59,130,246,.2)" },
  { bg: "#fef3c7", color: "#92400e", border: "rgba(245,158,11,.2)" },
  { bg: "#ede9fe", color: "#5b21b6", border: "rgba(139,92,246,.2)" },
];

export default function ExploreScreen() {
  const navigate = useNavigate();
  
  // ✅ Removed local useState and linked directly to global AppShellContext
  const { exploreChip: activeChip, setExploreChip: setActiveChip } = useAppShell();

  const [gratitudes, setGratitudes]   = useState([]);
  const [gratInput, setGratInput]     = useState("");
  const [gratLoading, setGratLoading] = useState(false);

  useEffect(() => {
    if (activeChip !== "gratitude" || !auth.currentUser) return;
    const uid    = auth.currentUser.uid;
    const colRef = collection(db, "gratitudeEntries", uid, "entries");
    const cutoff = Timestamp.fromMillis(Date.now() - 24 * 60 * 60 * 1000);

    onSnapshot(query(colRef, where("createdAt", "<", cutoff)), (snap) => {
      snap.docs.forEach((d) => deleteDoc(d.ref));
    });

    const unsub = onSnapshot(query(colRef, where("createdAt", ">=", cutoff)), (snap) => {
      setGratitudes(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis())
      );
    });
    return unsub;
  }, [activeChip]);

  const addGratitude = async () => {
    const wc = gratInput.trim().split(/\s+/).filter(Boolean).length;
    if (!gratInput.trim() || wc > 100 || !auth.currentUser) return;
    setGratLoading(true);
    try {
      await addDoc(collection(db, "gratitudeEntries", auth.currentUser.uid, "entries"), {
        text: gratInput.trim(),
        createdAt: Timestamp.now(),
      });
      setGratInput("");
    } catch (err) { console.error(err); }
    finally { setGratLoading(false); }
  };

  const deleteGratitude = async (id) => {
    if (!auth.currentUser) return;
    await deleteDoc(doc(db, "gratitudeEntries", auth.currentUser.uid, "entries", id));
  };

  return (
    <div className="hh-explore">

      {/* ═══ CHIP FILTER STRIP ═══ */}
      <div className="hh-chip-strip">
        {CHIPS.map(c => (
          <button
            key={c.id}
            className={`hh-chip ${activeChip === c.id ? "on" : "off"}`}
            onClick={() => setActiveChip(c.id)}
          >
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="hh-explore-content">

        {/* ── Stories → MobileJournal.jsx ── */}
        {activeChip === "stories" && (
          <div className="hh-explore-section" key="stories">
            <div className="hh-explore-component-wrap">
              <MobileJournal />
            </div>
          </div>
        )}

        {/* ── Games ── */}
        {activeChip === "games" && (
          <div className="hh-explore-section" key="games">
            <div className="hh-explore-pad">
              <div className="hh-game-featured" onClick={() => navigate("/cluecue")}>
                <div className="hh-game-feat-bg">🧮</div>
                <div className="hh-game-feat-badge">⭐ Featured</div>
                <div className="hh-game-feat-title">ClueCue 🎯</div>
                <div className="hh-game-feat-desc">Guess the celebrity from clues — 1v1 real-time!</div>
                <span className="hh-game-feat-cta">Quick Play →</span>
              </div>

              <div className="hh-section-label" style={{ marginTop: 16 }}>More Games</div>
              <div className="hh-mini-games-grid">
                {MINI_GAMES.map(g => (
                  <div 
                    key={g.id} 
                    className="hh-mini-game"
                    onClick={() => navigate(`/${g.id}`)} /* ✅ ADDED NAVIGATION LINK HERE */
                    role="button"
                    tabIndex={0}
                  >
                    <div className="hh-mg-emoji">{g.emoji}</div>
                    <div className="hh-mg-name">{g.name}</div>
                    <div className="hh-mg-desc">{g.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Gratitude Wall ── */}
        {activeChip === "gratitude" && (
          <div className="hh-explore-section" key="gratitude">
            <div className="hh-explore-pad">
              <div className="hh-section-label">🙏 gratitude wall</div>
              <p className="hh-grat-intro">Write what you're grateful for. Entries disappear after 24 hours. 🌅</p>

              <div className="hh-grat-input-wrap">
                <textarea
                  className="hh-grat-textarea"
                  placeholder="I'm grateful for…"
                  value={gratInput}
                  onChange={e => setGratInput(e.target.value)}
                  rows={2}
                />
                <div className="hh-grat-input-foot">
                  <span className={`hh-grat-wc${gratInput.trim().split(/\s+/).filter(Boolean).length > 100 ? " over" : ""}`}>
                    {gratInput.trim() === "" ? 0 : gratInput.trim().split(/\s+/).filter(Boolean).length}/100
                  </span>
                  <button
                    className="hh-grat-add-btn"
                    onClick={addGratitude}
                    disabled={gratLoading || !gratInput.trim() || gratInput.trim().split(/\s+/).filter(Boolean).length > 100}
                  >
                    {gratLoading ? "Saving…" : "Add ✨"}
                  </button>
                </div>
              </div>

              {gratitudes.length > 0 ? (
                <div className="hh-grat-grid">
                  {gratitudes.map((g, i) => {
                    const c = NOTE_COLORS[i % NOTE_COLORS.length];
                    const hoursLeft = Math.max(0, Math.floor((g.createdAt?.toMillis() + 24*60*60*1000 - Date.now()) / 3600000));
                    const minsLeft  = Math.max(0, Math.floor(((g.createdAt?.toMillis() + 24*60*60*1000 - Date.now()) % 3600000) / 60000));
                    return (
                      <div key={g.id} className="hh-grat-note" style={{ background: c.bg, color: c.color, borderColor: c.border }}>
                        <div className="hh-grat-text">🌟 {g.text}</div>
                        <div className="hh-grat-meta">
                          <span>⏳ {hoursLeft > 0 ? `${hoursLeft}h ${minsLeft}m` : `${minsLeft}m`} left</span>
                          <button className="hh-grat-del" onClick={() => deleteGratitude(g.id)}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="hh-grat-empty">Nothing yet — start with one thing, big or small! 🌱</div>
              )}
            </div>
          </div>
        )}

        {/* ── Exercises ── */}
        {activeChip === "exercises" && (
          <div className="hh-explore-section" key="exercises">
            <div className="hh-explore-component-wrap">
              <Exercises />
            </div>
          </div>
        )}

        {/* ── Jokes ── */}
        {activeChip === "jokes" && (
          <div className="hh-explore-section" key="jokes">
            <div className="hh-explore-component-wrap">
              <JokesSection />
            </div>
          </div>
        )}

        {/* ── Assessments ── */}
        {activeChip === "assess" && (
          <div className="hh-explore-section" key="assess">
            <div className="hh-explore-component-wrap">
              <WellnessTests />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}